import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { Button } from "../design-system";
import { setActiveWorkspaceId, useWorkspaces } from "../identity";
import { AnalyticsProvider } from "../analytics";
import { ConfigProvider } from "../configs";
import { AgentProvider } from "../agents";
import { InsightProvider } from "../insights";
import { BootScreen, DashboardBoot } from "../components/layout/DashboardBoot";
import { AppLoadingScreen } from "../components/layout/AppLoadingScreen";
import { WorkspaceProvider } from "./workspaceContext";
import { clearStoredWorkspaceId, storeWorkspaceId } from "./storage";

/* Route element for /w/:workspaceId. Validates the id against the viewer's
 * workspaces, publishes it as the active workspace (header + cache-key source),
 * and mounts the gateway data providers KEYED BY WORKSPACE — switching
 * workspaces remounts boot and every page, so nothing carries cross-workspace
 * state. An unknown id gets an explicit panel rather than an auto-redirect
 * (a poisoned stored id must not cause a redirect loop). */
export function WorkspaceShell() {
  const { workspaceId = "" } = useParams();
  const [version, setVersion] = useState(0);
  const { data: workspaces, error, initialLoading } = useWorkspaces(version);
  const refetchWorkspaces = useCallback(() => setVersion((v) => v + 1), []);

  const workspace = workspaces?.find((w) => w.id === workspaceId);

  // Publish during render, BEFORE children mount, so every fetch they fire
  // (effects run bottom-up) already carries the right workspace. Idempotent
  // module-state write, same spirit as useQuery's render-phase ref update.
  setActiveWorkspaceId(workspace?.id);

  useEffect(() => {
    if (workspace) storeWorkspaceId(workspace.id);
  }, [workspace]);

  const context = useMemo(
    () =>
      workspace && workspaces
        ? { workspace, workspaces, refetchWorkspaces }
        : null,
    [workspace, workspaces, refetchWorkspaces],
  );

  if (error) {
    return (
      <BootScreen>
        <p className="text-body-sm text-error-fg">
          Couldn’t load your workspaces. Check your connection and reload.
        </p>
      </BootScreen>
    );
  }

  if (initialLoading || !workspaces) {
    return <AppLoadingScreen />;
  }

  if (!context) return <WorkspaceNotFound />;

  return (
    <WorkspaceProvider value={context}>
      <AnalyticsProvider key={context.workspace.id}>
        <ConfigProvider>
          <AgentProvider>
            <InsightProvider>
              <DashboardBoot>
                <Outlet />
              </DashboardBoot>
            </InsightProvider>
          </AgentProvider>
        </ConfigProvider>
      </AnalyticsProvider>
    </WorkspaceProvider>
  );
}

function WorkspaceNotFound() {
  const navigate = useNavigate();
  return (
    <BootScreen>
      <div className="flex max-w-sm flex-col items-center gap-3 text-center">
        <p className="text-body font-medium text-fg-1">Workspace not found</p>
        <p className="text-body-sm text-fg-2">
          This workspace doesn’t exist or you don’t have access to it. Switch to
          one of your workspaces to continue.
        </p>
        <Button
          onClick={() => {
            clearStoredWorkspaceId();
            navigate("/", { replace: true });
          }}
        >
          Go to my workspace
        </Button>
      </div>
    </BootScreen>
  );
}
